import { useEffect, useState, type ReactNode } from 'react';
import { AppLoadingSplash } from '@eureca/ui';
import { setAccessTokenProvider, setUnauthorizedHandler } from '@eureca/recruiter-api-sdk/client';
import { SessionExpiredScreen } from '@eureca/ui';
import { AccessRequestedFeedback } from '@/features/login/AccessRequestedFeedback';
import { ensureKeycloakInitialized, getKeycloak } from './keycloak-instance';

type GateState = 'loading' | 'ready' | 'error' | 'access_requested' | 'session_expired';

let accessDeniedDetected = false;

// keycloak-js defaults to `responseMode: 'fragment'`, so a successful auth
// (login OR self-registration) returns to the SPA with the callback params in
// the URL *fragment* (`#code=...&state=...`), NOT the query string. Read both
// so detection works regardless of response mode — checking only
// `location.search` silently misses the fragment and falls through to the
// generic init-error screen.
function readCallbackParams(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams();
  const merged = new URLSearchParams(window.location.search);
  const hash = window.location.hash.replace(/^#/, '');
  if (hash) {
    for (const [key, value] of new URLSearchParams(hash)) {
      if (!merged.has(key)) merged.set(key, value);
    }
  }
  return merged;
}

// A successful Keycloak auth (login OR self-registration) returns to the SPA
// with a `code`. Captured at module load — BEFORE keycloak-js processes and
// strips the callback params. If the subsequent token exchange then fails, the
// most likely cause for the recruiters realm is a user who registered but is
// still pending approval: the account is created disabled, so KC issues the
// code yet the exchange returns `invalid_grant` / "User disabled". In that case
// we show the "acesso solicitado" screen — mirroring the Google
// first-broker-login flow, which redirects with `error=access_denied`. A code
// can't be exchanged in a registration-completed flow without this state, so
// "had a code AND init failed" is a reliable signal (an approved login resolves
// init successfully and never reaches the catch; a genuine infra failure — KC
// down — never returns a code in the first place).
const returnedWithAuthCode = readCallbackParams().has('code');

function detectAccessDenied(): boolean {
  if (accessDeniedDetected) return true;
  const params = readCallbackParams();
  if (params.get('error') === 'access_denied') {
    accessDeniedDetected = true;
    window.history.replaceState({}, '', '/login');
    return true;
  }
  return false;
}

// Both the access-token provider catch and the SDK's 401 handler can decide
// to re-authenticate at the same time — N parallel queries that all fail
// `updateToken(30)` would otherwise call `keycloak.login()` N times,
// triggering N concurrent location changes (the actual reload-loop symptom).
let loginInFlight = false;

function triggerKeycloakLogin(keycloak: ReturnType<typeof getKeycloak>): void {
  if (loginInFlight) return;
  loginInFlight = true;
  keycloak.login({ redirectUri: window.location.origin + '/' });
}

export function KeycloakGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>(() =>
    detectAccessDenied() ? 'access_requested' : 'loading'
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (state === 'access_requested') return;

    let cancelled = false;

    (async () => {
      try {
        const authenticated = await ensureKeycloakInitialized();
        if (cancelled) return;

        if (!authenticated) {
          setAccessTokenProvider(null);
          setUnauthorizedHandler(null);
          setState('ready');
          return;
        }

        const keycloak = getKeycloak();

        // EU-686 (RN-23/24) — sessão expirada NÃO redireciona em silêncio:
        // a tela global "Você foi desconectado" assume IMEDIATAMENTE (o gate
        // envolve o router, então ela tem precedência sobre skeleton/erro
        // localizado de qualquer aba) e sai SEM o menu lateral — não existe
        // menu deslogado no App Consultor.
        const showSessionExpired = () => {
          if (!cancelled) setState('session_expired');
        };

        setAccessTokenProvider(async () => {
          try {
            await keycloak.updateToken(30);
            return keycloak.token ?? undefined;
          } catch {
            showSessionExpired();
            return undefined;
          }
        });

        setUnauthorizedHandler(() => {
          showSessionExpired();
        });

        setState('ready');
      } catch (e) {
        if (cancelled) return;
        // Auth succeeded at KC (we came back with a code) but the token
        // exchange failed → recruiter registered, pending approval (disabled
        // user). Show "acesso solicitado" instead of a generic init error.
        if (returnedWithAuthCode) {
          // Clear the stale ?code so a refresh doesn't re-run the failing
          // exchange (and so the address bar matches the login route).
          window.history.replaceState({}, '', '/login');
          setState('access_requested');
          return;
        }
        setErrorMessage(e instanceof Error ? e.message : 'Keycloak init failed');
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
      setAccessTokenProvider(null);
      setUnauthorizedHandler(null);
      // Reset so a later remount (StrictMode double-invoke, HMR, route change
      // that unmounts the gate) is not permanently locked out of re-login.
      loginInFlight = false;
    };
  }, []);

  if (state === 'access_requested') {
    return <AccessRequestedFeedback />;
  }

  if (state === 'session_expired') {
    // RN-24 — full-area, sem ConsultantLayout/menu (HAV-024/025).
    return (
      <SessionExpiredScreen
        platform="consultants"
        onLogin={() => triggerKeycloakLogin(getKeycloak())}
        onGoHome={() => {
          window.location.href = '/';
        }}
      />
    );
  }

  if (state === 'error') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-muted px-4">
        <div className="flex w-full max-w-sm flex-col items-center gap-6 text-center">
          <p className="text-sm text-muted-foreground">
            Não foi possível inicializar a autenticação.
            {errorMessage ? ` (${errorMessage})` : ''}
          </p>
          <button
            type="button"
            onClick={() => {
              // Wipe local SPA state. JS only reaches same-origin storage —
              // Keycloak's SSO session cookie lives on the Keycloak host
              // (HttpOnly, cross-origin) so we hand that off to KC's logout
              // endpoint below.
              try {
                window.localStorage.clear();
              } catch {
                // ignore — quota / disabled storage
              }
              try {
                window.sessionStorage.clear();
              } catch {
                // ignore
              }
              for (const entry of document.cookie.split(';')) {
                const name = entry.split('=')[0]?.trim();
                if (!name) continue;
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
              }
              loginInFlight = false;
              // Route through Keycloak so KEYCLOAK_SESSION is cleared
              // server-side — otherwise check-sso re-picks up the SSO session
              // on the next page load and we land in the same loop.
              const keycloak = getKeycloak();
              const postLogoutRedirectUri = window.location.origin + '/login';
              try {
                keycloak.logout({ redirectUri: postLogoutRedirectUri });
              } catch {
                window.location.href = postLogoutRedirectUri;
              }
            }}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Voltar para o login
          </button>
        </div>
      </div>
    );
  }

  if (state === 'loading') {
    // Shared global loading skeleton (pulsing Eureca mark) while Keycloak boots.
    return <AppLoadingSplash label="Entrando" />;
  }

  return <>{children}</>;
}
